import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import axios from "../../utils/axiosConfig";
import { useUser } from "../../context/UserContext";
import RegistrationSurvey from "../questionary/RegistrationSurvey.js";

import "../../styles/features/events/EventDetails.css";

const PARTY_CONFIG = {
  dinner: {
    category: "DINNER",
    title: "Kolacje w kameralnym gronie",
    subtitle:
      "Kameralne spotkania przy wspólnym stole, stworzone po to, żeby poznawać nowych ludzi bez presji.",
    fallbackImage: "/imagesEvents/party5.jpg",
  },

  wine: {
    category: "WINE",
    title: "Spotkania przy winie",
    subtitle:
      "Luźny wieczór przy winie, rozmowach i poznawaniu nowych osób w swobodnej atmosferze.",
    fallbackImage: "/images/party3.jpg",
  },
};

export default function PartyEventDetails() {
  const navigate = useNavigate();
  const { partyCategory } = useParams();

  const {
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();

  const { user, profileLoading, profileLoaded } = useUser();

  const config = PARTY_CONFIG[partyCategory];

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");

  const [activeImg, setActiveImg] = useState(0);

  const [joining, setJoining] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [joinMessage, setJoinMessage] = useState("");
  const [showRegistrationSurvey, setShowRegistrationSurvey] =
    useState(false);

  /*
   * Pobieramy wszystkie nadchodzące PARTY.
   * Potem filtrujemy po partyCategory:
   * DINNER albo WINE.
   */
  useEffect(() => {
    if (!config) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadEvents = async () => {
      try {
        setLoading(true);

        const response = await axios.get("/api/events", {
          params: {
            type: "PARTY",
            scope: "UPCOMING",
          },
        });

        if (!mounted) {
          return;
        }

        const data = Array.isArray(response.data)
          ? response.data
          : [];

        const categoryEvents = data.filter(
          (event) =>
            event.partyCategory === config.category
        );

        setEvents(categoryEvents);
      } catch (error) {
        console.error(
          "Failed to load party events:",
          error
        );

        if (mounted) {
          setEvents([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      mounted = false;
    };
  }, [config]);

  /*
   * Lista miast buduje się automatycznie
   * na podstawie eventów istniejących w DB.
   */
  const cities = useMemo(() => {
    return [
      ...new Set(
        events
          .map((event) => event.city)
          .filter(Boolean)
      ),
    ].sort((a, b) =>
      a.localeCompare(b, "pl")
    );
  }, [events]);

  /*
   * Po wyborze miasta pokazujemy tylko
   * eventy dostępne w tym mieście.
   */
  const availableEvents = useMemo(() => {
    if (!selectedCity) {
      return [];
    }

    return events
      .filter(
        (event) =>
          event.city === selectedCity
      )
      .sort((a, b) => {
        const first = `${a.date || ""} ${a.time || ""}`;
        const second = `${b.date || ""} ${b.time || ""}`;

        return first.localeCompare(second);
      });
  }, [events, selectedCity]);

  /*
   * Wybrany termin = konkretny rekord Event z DB.
   */
  const selectedEvent = useMemo(() => {
    if (!selectedEventId) {
      return null;
    }

    return (
      events.find(
        (event) =>
          String(event.id) ===
          String(selectedEventId)
      ) || null
    );
  }, [events, selectedEventId]);

  /*
   * Do zdjęć/opisu możemy używać pierwszego eventu
   * danej kategorii, bo wszystkie DINNER powinny
   * mieć ten sam charakter strony.
   *
   * Po wyborze terminu wykorzystamy wybrany event.
   */
  const displayEvent =
    selectedEvent || events[0] || null;

  const images = useMemo(() => {
    if (
      displayEvent?.images &&
      displayEvent.images.length > 0
    ) {
      return displayEvent.images;
    }

    return [config?.fallbackImage];
  }, [displayEvent, config]);

  useEffect(() => {
    setActiveImg(0);
  }, [selectedEventId]);

  const priceLabel = useMemo(() => {
    if (!selectedEvent) {
      return "";
    }

    if (selectedEvent.price == null) {
      return "";
    }

    return `${selectedEvent.price} PLN`;
  }, [selectedEvent]);

  const getJoinButtonConfig = (event) => {
    if (!event) {
      return {
        label: "Wybierz miasto i termin",
        disabled: true,
      };
    }

    const state = (
      event.signupState || "OPEN"
    ).toUpperCase();

    const hasSpots =
      (event.spotsLeft ?? 0) > 0;

    if (
      event.accessType ===
      "INVITATION_ONLY"
    ) {
      return {
        label: "Wydarzenie na zaproszenie",
        disabled: true,
      };
    }

    if (!hasSpots) {
      return {
        label: "Brak miejsc",
        disabled: true,
      };
    }

    if (state === "SOON") {
      return {
        label: "Zapisy wkrótce",
        disabled: true,
      };
    }

    if (state === "CLOSED") {
      return {
        label: "Zapisy zakończone",
        disabled: true,
      };
    }

    if (
      event.accessType ===
      "GUESTS_ALLOWED"
    ) {
      return {
        label: "Zarezerwuj miejsce",
        disabled: false,
      };
    }

    if (
      event.accessType ===
      "REGISTERED_USERS"
    ) {
      return {
        label: "Zapisz się",
        disabled: false,
      };
    }

    return {
      label: "Niedostępne",
      disabled: true,
    };
  };

  const joinButton =
    getJoinButtonConfig(selectedEvent);

  const handleCityChange = (event) => {
    setSelectedCity(event.target.value);

    // ważne:
    // po zmianie miasta zerujemy poprzednią datę
    setSelectedEventId("");
    setRegistered(false);
    setJoinMessage("");
  };

  const handleEventChange = (event) => {
    setSelectedEventId(
      event.target.value
    );

    setRegistered(false);
    setJoinMessage("");
  };

  const handleJoin = async () => {
    if (
      !selectedEvent ||
      joinButton.disabled ||
      joining
    ) {
      return;
    }

    /*
     * Rejestracja bez konta.
     */
    if (
      selectedEvent.accessType ===
      "GUESTS_ALLOWED"
    ) {
      navigate(
        `/events/${selectedEvent.id}/signup`
      );
      return;
    }

    /*
     * Rejestracja dla zalogowanych.
     */
    if (
      selectedEvent.accessType ===
      "REGISTERED_USERS"
    ) {
      if (!isAuthenticated) {
        await loginWithRedirect({
          appState: {
            returnTo: `/events/party/${partyCategory}`,
          },
        });

        return;
      }

      if (
        profileLoading ||
        !profileLoaded
      ) {
        return;
      }

      if (!user?.profileCompleted) {
        navigate("/onboarding", {
          state: {
            from: `/events/party/${partyCategory}`,
          },
        });

        return;
      }

      try {
        setJoining(true);
        setJoinMessage("");

        const token =
          await getAccessTokenSilently({
            authorizationParams: {
              audience:
                "https://someet-api",
            },
          });

        const response = await axios.post(
          `/api/events/${selectedEvent.id}/registrations`,
          {},
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

        setRegistered(true);

        setShowRegistrationSurvey(true);

        if (response.data.emailSent) {
          setJoinMessage(
            "Gotowe! Twoje miejsce zostało zarezerwowane 🎉 Instrukcję płatności wysłaliśmy na Twój adres e-mail."
          );
        } else {
          setJoinMessage(
            "Twoje miejsce zostało zarezerwowane 🎉 Nie udało nam się jednak wysłać wiadomości e-mail. Skontaktuj się z nami."
          );
        }
      } catch (error) {
        setJoinMessage(
          error.response?.data?.message ||
            "Nie udało się zapisać na wydarzenie. Spróbuj ponownie."
        );
      } finally {
        setJoining(false);
      }
    }
  };

  if (!config) {
    return (
      <div className="eventDetails page">
        <div className="eventDetails__container">
          <h2>
            Nie znaleziono tego rodzaju
            spotkania.
          </h2>

          <button
            className="eventDetails__back"
            onClick={() =>
              navigate("/events/party")
            }
          >
            ← Wróć
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="eventDetails page">
        <div className="eventDetails__container">
          <div className="eventDetails__skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="eventDetails page">
      <div className="eventDetails__container">

        {/* TOP */}
        <div className="eventDetails__top">
          <button
            className="eventDetails__back"
            onClick={() =>
              navigate("/events/party")
            }
          >
            ← Spotkania
          </button>

          <div className="eventDetails__typeBadge">
            Spotkanie
          </div>
        </div>

        <div className="eventDetails__grid">

          {/* LEWA STRONA */}
          <div className="eventDetails__left">

            {/* GALERIA */}
            <div className="eventDetails__gallery">
              <div className="eventDetails__mainImgWrap">
                <img
                  className="eventDetails__mainImg"
                  src={images[activeImg]}
                  alt={config.title}
                />
              </div>

              {images.length > 1 && (
                <div className="eventDetails__thumbs">
                  {images.map(
                    (src, index) => (
                      <button
                        key={`${src}-${index}`}
                        type="button"
                        className={`eventDetails__thumbBtn ${
                          index === activeImg
                            ? "isActive"
                            : ""
                        }`}
                        onClick={() =>
                          setActiveImg(index)
                        }
                      >
                        <img
                          className="eventDetails__thumbImg"
                          src={src}
                          alt=""
                        />
                      </button>
                    )
                  )}
                </div>
              )}
            </div>

            {/* HEADER */}
            <div className="eventDetails__header">
              <h1 className="eventDetails__title">
                {config.title}
              </h1>

              <p className="eventDetails__subtitle">
                {displayEvent?.subtitle ||
                  config.subtitle}
              </p>

              <div className="eventDetails__metaRow">
                {selectedEvent ? (
                  <>
                    <span className="pill">
                      📍 {selectedEvent.city}
                      {selectedEvent.place
                        ? ` · ${selectedEvent.place}`
                        : ""}
                    </span>

                    <span className="pill">
                      🗓️{" "}
                      {formatEventDate(
                        selectedEvent.date
                      )}
                      {selectedEvent.time
                        ? ` · ${selectedEvent.time}`
                        : ""}
                    </span>

                    {selectedEvent.durationMin ? (
                      <span className="pill">
                        ⏳{" "}
                        {
                          selectedEvent.durationMin
                        }{" "}
                        min
                      </span>
                    ) : null}

                    {selectedEvent.ageLimit ? (
                      <span className="pill">
                        🔞{" "}
                        {selectedEvent.ageLimit}+
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="pill">
                    📍 Wybierz miasto i termin
                  </span>
                )}
              </div>

              {!!displayEvent?.tags?.length && (
                <div className="eventDetails__tags">
                  {displayEvent.tags.map(
                    (tag) => (
                      <span
                        key={tag}
                        className="tag"
                      >
                        #{tag}
                      </span>
                    )
                  )}
                </div>
              )}
            </div>

            {/* OPIS */}
            {displayEvent?.description && (
              <section className="eventDetails__section">
                <h3>Opis</h3>

                <p className="eventDetails__text">
                  {
                    displayEvent.description
                  }
                </p>
              </section>
            )}

            {/* CO JEST W CENIE */}
            {!!displayEvent?.included?.length && (
              <section className="eventDetails__section">
                <h3>Co jest w cenie</h3>

                <ul className="eventDetails__list">
                  {displayEvent.included.map(
                    (item) => (
                      <li key={item}>
                        {item}
                      </li>
                    )
                  )}
                </ul>
              </section>
            )}

            {/* PLAN */}
            {!!displayEvent?.agenda?.length && (
              <section className="eventDetails__section">
                <h3>Jak wygląda spotkanie?</h3>

                <div className="eventDetails__agenda">
                  <ul className="eventDetails__list">
                    {displayEvent.agenda.map(
                      (item) => (
                        <li key={item}>
                          {item}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </section>
            )}

            {/* FAQ */}
            {!!displayEvent?.faq?.length && (
              <section className="eventDetails__section">
                <h3>FAQ</h3>

                <div className="eventDetails__faq">
                  {displayEvent.faq.map(
                    (item) => (
                      <details
                        key={item.q}
                        className="eventDetails__faqItem"
                      >
                        <summary>
                          {item.q}
                        </summary>

                        <p>{item.a}</p>
                      </details>
                    )
                  )}
                </div>
              </section>
            )}
          </div>

          {/* PRAWA STRONA */}
          <aside className="eventDetails__right">
            <div className="eventDetails__card">

              <h3 className="partyBooking__title">
                Wybierz swoje spotkanie
              </h3>

              {/* MIASTO */}
              <div className="partyBooking__field">
                <label
                  htmlFor="party-city"
                  className="partyBooking__label"
                >
                  Miasto
                </label>

                <select
                  id="party-city"
                  className="partyBooking__select"
                  value={selectedCity}
                  onChange={
                    handleCityChange
                  }
                >
                  <option value="">
                    Wybierz miasto
                  </option>

                  {cities.map((city) => (
                    <option
                      key={city}
                      value={city}
                    >
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              {/* TERMIN */}
              <div className="partyBooking__field">
                <label
                  htmlFor="party-date"
                  className="partyBooking__label"
                >
                  Termin
                </label>

                <select
                  id="party-date"
                  className="partyBooking__select"
                  value={selectedEventId}
                  onChange={
                    handleEventChange
                  }
                  disabled={!selectedCity}
                >
                  <option value="">
                    {selectedCity
                      ? "Wybierz termin"
                      : "Najpierw wybierz miasto"}
                  </option>

                  {availableEvents.map(
                    (event) => (
                      <option
                        key={event.id}
                        value={event.id}
                      >
                        {formatEventDate(
                          event.date
                        )}
                        {event.time
                          ? ` · ${event.time}`
                          : ""}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* INFO O WYBRANYM EVENCIE */}
              {selectedEvent && (
                <>
                  <div className="eventDetails__priceRow">
                    <div>
                      <div className="eventDetails__price">
                        {priceLabel}
                      </div>
                    </div>

                    <div
                      className={`eventDetails__status ${
                        joinButton.disabled
                          ? "full"
                          : "ok"
                      }`}
                    >
                      {registered
                        ? "Zapisana"
                        : joinButton.disabled
                          ? "Niedostępne"
                          : "Dostępne"}
                    </div>
                  </div>

                  {selectedEvent.address && (
                    <div className="eventDetails__info">
                      <div className="eventDetails__infoRow">
                        <span className="muted">
                          Miejsce
                        </span>

                        <span>
                          {
                            selectedEvent.address
                          }
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              <button
                type="button"
                className={`eventDetails__cta ${
                  joinButton.disabled ||
                  registered
                    ? "isDisabled"
                    : ""
                }`}
                onClick={handleJoin}
                disabled={
                  joinButton.disabled ||
                  joining ||
                  registered
                }
              >
                {registered
                  ? "Jesteś zapisana ✓"
                  : joining
                    ? "Zapisywanie..."
                    : joinButton.label}
              </button>

              {joinMessage && (
                <p className="eventDetails__message">
                  {joinMessage}
                </p>
              )}

              {selectedEvent?.accessType ===
                "INVITATION_ONLY" && (
                <p className="eventDetails__note">
                  To wydarzenie jest
                  dostępne tylko po
                  otrzymaniu zaproszenia.
                </p>
              )}

              {displayEvent?.rulesNote && (
                <p className="eventDetails__note">
                  {displayEvent.rulesNote}
                </p>
              )}
            </div>
          </aside>
        </div>

        {/* ANKIETA PO ZAPISIE */}
        {showRegistrationSurvey &&
          selectedEvent?.accessType ===
            "REGISTERED_USERS" && (
            <div
              className="registrationSurveyModal__overlay"
              onClick={() =>
                setShowRegistrationSurvey(
                  false
                )
              }
            >
              <div
                className="registrationSurveyModal"
                onClick={(event) =>
                  event.stopPropagation()
                }
              >
                <button
                  type="button"
                  className="registrationSurveyModal__close"
                  onClick={() =>
                    setShowRegistrationSurvey(
                      false
                    )
                  }
                  aria-label="Zamknij"
                >
                  ×
                </button>

                <RegistrationSurvey
                  eventId={
                    selectedEvent.id
                  }
                  onFinished={() =>
                    setShowRegistrationSurvey(
                      false
                    )
                  }
                />
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

function formatEventDate(date) {
  if (!date) {
    return "Termin wkrótce";
  }

  /*
   * Dla "2026-09-03".
   * Dodajemy godzinę, żeby uniknąć problemów
   * ze zmianą dnia przez timezone.
   */
  const parsedDate = new Date(
    `${date}T12:00:00`
  );

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat(
    "pl-PL",
    {
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  ).format(parsedDate);
}